import React from 'react';
import { Head, useForm, Link } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ArrowLeft, Send, AlertCircle } from 'lucide-react';

interface NewProps {
  errors?: Record<string, string>;
  oldValues?: {
    name?: string;
    slug?: string;
    description?: string;
    access_type?: string;
    subscription_type?: string;
    delivery_schedule?: string;
    is_active?: boolean;
    require_confirmation?: boolean;
    sender_name?: string;
    sender_email?: string;
  };
}

const New: React.FC<NewProps> = ({ oldValues = {} }) => {
  const { data, setData, post, processing, errors: formErrors } = useForm({
    name: oldValues.name || '',
    slug: oldValues.slug || '',
    description: oldValues.description || '',
    access_type: oldValues.access_type || 'open_access',
    subscription_type: oldValues.subscription_type || 'open_subscription',
    delivery_schedule: oldValues.delivery_schedule || 'weekly',
    is_active: oldValues.is_active ?? true,
    require_confirmation: oldValues.require_confirmation ?? true,
    sender_name: oldValues.sender_name || '',
    sender_email: oldValues.sender_email || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post('/admin/newsletters');
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setData('name', name);
    // Auto-generate slug if empty
    if (!data.slug) {
      setData('slug', generateSlug(name));
    }
  };

  return (
    <DashboardLayout>
      <Head title="Create Newsletter" />

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-4">
          <Link href="/admin/newsletters">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Create Newsletter</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Create a new newsletter campaign to engage your audience.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {Object.keys(formErrors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                <AlertCircle className="h-4 w-4" />
                Please fix the following errors:
              </div>
              <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                {Object.entries(formErrors).map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
              </ul>
            </div>
          )}
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader>
              <CardTitle>Newsletter Details</CardTitle>
              <CardDescription>
                Basic information about your newsletter.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  type="text"
                  value={data.name}
                  onChange={handleNameChange}
                  placeholder="e.g., Weekly Tech Digest"
                  required
                />
                {formErrors.name && <p className="text-sm text-red-500">{formErrors.name}</p>}
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  type="text"
                  value={data.slug}
                  onChange={(e) => setData('slug', e.target.value)}
                  placeholder="e.g., weekly-tech-digest"
                  required
                />
                {formErrors.slug && <p className="text-sm text-red-500">{formErrors.slug}</p>}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  placeholder="Describe what this newsletter is about..."
                  rows={3}
                />
                {formErrors.description && <p className="text-sm text-red-500">{formErrors.description}</p>}
              </div>

              {/* Access Type */}
              <div className="space-y-2">
                <Label htmlFor="access_type">Access Type</Label>
                <Select
                  value={data.access_type}
                  onValueChange={(value) => setData('access_type', value as 'open_access' | 'restricted_access')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select access type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open_access">Open Access (Public)</SelectItem>
                    <SelectItem value="restricted_access">Restricted (Authenticated Users Only)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Open Access allows anyone to subscribe. Restricted limits access to logged-in users.
                </p>
              </div>

              {/* Subscription Type */}
              <div className="space-y-2">
                <Label htmlFor="subscription_type">Subscription Type</Label>
                <Select
                  value={data.subscription_type}
                  onValueChange={(value) => setData('subscription_type', value as 'open_subscription' | 'auth_required')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subscription type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open_subscription">Open Subscription (Email-only)</SelectItem>
                    <SelectItem value="auth_required">Authentication Required</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Open Subscription allows email-only signups. Authentication Required needs a user account.
                </p>
              </div>

              {/* Delivery Schedule */}
              <div className="space-y-2">
                <Label htmlFor="delivery_schedule">Delivery Schedule</Label>
                <Select
                  value={data.delivery_schedule}
                  onValueChange={(value) => setData('delivery_schedule', value as 'manual' | 'daily' | 'weekly' | 'monthly')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select delivery schedule" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sender Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender_name">Sender Name</Label>
                  <Input
                    id="sender_name"
                    type="text"
                    value={data.sender_name}
                    onChange={(e) => setData('sender_name', e.target.value)}
                    placeholder="e.g., Your Company"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender_email">Sender Email</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    value={data.sender_email}
                    onChange={(e) => setData('sender_email', e.target.value)}
                    placeholder="e.g., news@example.com"
                  />
                </div>
              </div>

              {/* Toggles */}
              <div className="flex items-center justify-between space-y-2">
                <div className="space-y-0.5">
                  <Label htmlFor="is_active">Active</Label>
                  <p className="text-xs text-muted-foreground">
                    Allow subscriptions to this newsletter
                  </p>
                </div>
                <Switch
                  id="is_active"
                  checked={data.is_active}
                  onCheckedChange={(checked) => setData('is_active', checked)}
                />
              </div>

              <div className="flex items-center justify-between space-y-2">
                <div className="space-y-0.5">
                  <Label htmlFor="require_confirmation">Require Confirmation</Label>
                  <p className="text-xs text-muted-foreground">
                    Send confirmation email before subscribing
                  </p>
                </div>
                <Switch
                  id="require_confirmation"
                  checked={data.require_confirmation}
                  onCheckedChange={(checked) => setData('require_confirmation', checked)}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Link href="/admin/newsletters">
                <Button variant="outline" type="button">Cancel</Button>
              </Link>
              <Button type="submit" disabled={processing}>
                <Send className="h-4 w-4 mr-2" />
                {processing ? 'Creating...' : 'Create Newsletter'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default New;
